import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  const businessServices = [
    { name: 'Corporate & Business Contracts', slug: 'corporate-business-contracts' },
    { name: 'Corporate & Business Formation', slug: 'corporate-business-formation' },
    { name: 'Business Transaction', slug: 'business-transaction' },
    { name: 'Mergers & Acquisitions', slug: 'mergers-acquisitions' },
    { name: 'Government Contracting', slug: 'government-contracting' },
    { name: 'Business Disputes', slug: 'business-disputes' },
  ];

  const personalServices = [
    { name: 'Immigration', slug: 'immigration' },
    { name: 'Trademark Registration', slug: 'trademark-registration' },
    { name: 'Copyright Registration', slug: 'copyright-registration' },
    { name: 'Non-Profit Law', slug: 'non-profit-law' },
    { name: 'Anti-Corruption Compliance', slug: 'anti-corruption-compliance' },
  ];

  return (
    <footer className="bg-black text-white relative overflow-hidden" data-testid="main-footer">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      </div>
      
      <div className="relative">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Brand Section - Takes 4 columns */}
            <div className="lg:col-span-4">
              <Link to="/" className="flex items-center mb-6">
                <img 
                  src="/images/logo-footer.png" 
                  alt="Zorvian Associates" 
                  className="h-16 w-auto"
                />
              </Link>
              <p className="text-slate-300 text-base leading-relaxed">
                Your trusted partner in legal matters. We provide comprehensive legal services with integrity, professionalism, and dedication to achieving the best outcomes for our clients.
              </p>
            </div>

            {/* Services Section - Takes 3 columns */}
            <div className="lg:col-span-3">
              <h3 className="text-xl font-bold font-heading mb-6 flex items-center">
                <span className="w-1 h-6 bg-[#2563EB] mr-3"></span>
                Legal Services
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/services/corporate-business-contracts" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    Corporate Law
                  </Link>
                </li>
                <li>
                  <Link to="/services/mergers-acquisitions" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    M&A Advisory
                  </Link>
                </li>
                <li>
                  <Link to="/services/immigration" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    Immigration Law
                  </Link>
                </li>
                <li>
                  <Link to="/services/trademark-registration" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    IP Protection
                  </Link>
                </li>
                <li>
                  <Link to="/services/business-disputes" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    Business Litigation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Section - Takes 2 columns */}
            <div className="lg:col-span-2">
              <h3 className="text-xl font-bold font-heading mb-6 flex items-center">
                <span className="w-1 h-6 bg-[#2563EB] mr-3"></span>
                Company
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/about-us" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/our-attorneys" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    Our Team
                  </Link>
                </li>
                <li>
                  <Link to="/contact-us" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    Contact
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-slate-300 hover:text-[#2563EB] transition-colors flex items-center group">
                    <span className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3 group-hover:bg-[#2563EB] transition-colors"></span>
                    Careers
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Section - Takes 3 columns */}
            <div className="lg:col-span-3">
              <h3 className="text-xl font-bold font-heading mb-6 flex items-center">
                <span className="w-1 h-6 bg-[#2563EB] mr-3"></span>
                Get In Touch
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-[#2563EB]/20 p-2 rounded-lg mt-0.5">
                    <MapPin className="h-5 w-5 text-[#2563EB]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Visit Us</p>
                    <p className="text-slate-300 text-sm">
                      166 Geary St Ste 1500 #1787<br />
                      San Francisco, CA 94108
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-[#2563EB]/20 p-2 rounded-lg mt-0.5">
                    <Phone className="h-5 w-5 text-[#2563EB]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Call Us</p>
                    <a href="tel:+18449212228" className="text-slate-300 hover:text-[#2563EB] text-sm transition-colors block">
                      +1 (844) 921-2228
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-[#2563EB]/20 p-2 rounded-lg mt-0.5">
                    <Mail className="h-5 w-5 text-[#2563EB]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Email Us</p>
                    <a href="mailto:Info@zorvianassociates.com" className="text-slate-300 hover:text-[#2563EB] text-sm transition-colors">
                      Info@zorvianassociates.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-center md:text-left">
                <p className="text-slate-400 text-sm">
                  © {new Date().getFullYear()} Zorvian Associates. All rights reserved.
                </p>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <Link to="/privacy-policy" className="text-slate-400 hover:text-[#2563EB] transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-slate-600">•</span>
                <Link to="/terms-of-service" className="text-slate-400 hover:text-[#2563EB] transition-colors">
                  Terms of Service
                </Link>
                <span className="text-slate-600">•</span>
                <a href="#" className="text-slate-400 hover:text-[#2563EB] transition-colors">
                  Cookie Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
